import { PERMISSIONS } from './permissions.js';
/**
 * Sellable modules.
 *
 * A module is a commercial line item: a tenant either pays for it or does not.
 * The catalogue lives in code because it is tied to shipped features — a module
 * naming permissions no service implements is a lie the database cannot catch.
 * Which tenant holds which is data, because that is tied to a contract signed
 * at runtime; it lives in `tenants.enabled_modules`.
 *
 * `CORE` is never sold and never stored. It is unioned in at resolution time so
 * no operator can save a tenant into a state where its own administrators
 * cannot manage users, roles or read their own audit trail.
 */
export const MODULES = {
    CORE: 'CORE',
    PROPERTY: 'PROPERTY',
    BOOKING: 'BOOKING',
    PRICING: 'PRICING',
    PROMOTIONS: 'PROMOTIONS',
    PAYMENTS: 'PAYMENTS',
    RULES: 'RULES',
    NOTIFICATIONS: 'NOTIFICATIONS',
    PMS: 'PMS',
};
/** Every module code, for a Zod enum at a route boundary. */
export const MODULE_CODES = Object.values(MODULES);
/** Always granted, never stored, never disableable. */
export const CORE_MODULES = [MODULES.CORE];
/**
 * Permissions that belong to no module.
 *
 * Held only by platform operators, who sit outside every tenant and therefore
 * skip the module intersection entirely. Keeping them out of every module is
 * what makes it impossible for a module toggle to hand tenant-lifecycle
 * authority to a tenant's own principal — including through the seeded
 * `Super Administrator` role, which holds every permission in the list.
 */
export const PLATFORM_PERMISSIONS = [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_CREATE,
    PERMISSIONS.TENANT_UPDATE,
    PERMISSIONS.TENANT_SUSPEND,
    PERMISSIONS.TENANT_DELETE,
    PERMISSIONS.TENANT_IMPERSONATE,
    PERMISSIONS.TENANT_USER_MANAGE,
    PERMISSIONS.TENANT_MODULE_ASSIGN,
    // Reference data is platform-wide, so Write and Import are platform-operator
    // permissions — a tenant able to edit it edits it for everyone.
    PERMISSIONS.REFERENCE_WRITE,
    PERMISSIONS.REFERENCE_IMPORT,
];
/**
 * Which permissions each module contains.
 *
 * Together with `PLATFORM_PERMISSIONS` this is an exact partition of
 * `PERMISSIONS`: every permission in one bucket, none missing, none twice.
 * `tests/modules.test.ts` asserts it, and that test is the reason a permission
 * added without a home fails the build instead of silently defaulting into
 * every tenant's grant — the failure mode the deny-lists in
 * `packages/database/src/seed/iam.ts` still carry a HAZARD note about.
 */
export const MODULE_PERMISSIONS = {
    [MODULES.CORE]: [
        PERMISSIONS.USER_READ,
        PERMISSIONS.USER_WRITE,
        PERMISSIONS.ROLE_READ,
        PERMISSIONS.ROLE_WRITE,
        PERMISSIONS.AUDIT_READ,
        // Reservation summary: a cross-property dashboard that is a core operational view,
        // not a separately sold module. Gated separately from Reservation.Read because it
        // surfaces aggregate figures rather than individual records.
        PERMISSIONS.RESERVATION_DASHBOARD,
        // Attachments sit in CORE rather than in a sellable module: property
        // photographs, guest identity documents and reservation paperwork all go
        // through the same service, so gating them on a module would break three
        // unrelated features at once for any tenant that had not bought it.
        PERMISSIONS.ATTACHMENT_READ,
        PERMISSIONS.ATTACHMENT_WRITE,
        PERMISSIONS.ATTACHMENT_DELETE,
        // Reference vocabularies are needed by every tenant's property, tenant and
        // guest forms, so Read sits in CORE rather than behind a module toggle.
        PERMISSIONS.REFERENCE_READ,
    ],
    [MODULES.PROPERTY]: [
        PERMISSIONS.PROPERTY_READ,
        PERMISSIONS.PROPERTY_CREATE,
        PERMISSIONS.PROPERTY_UPDATE,
        PERMISSIONS.PROPERTY_DELETE,
        PERMISSIONS.PROPERTY_CONFIGURE,
        PERMISSIONS.PROPERTY_SETUP,
        PERMISSIONS.PROPERTY_GROUP_READ,
        PERMISSIONS.PROPERTY_GROUP_CREATE,
        PERMISSIONS.PROPERTY_GROUP_UPDATE,
        PERMISSIONS.PROPERTY_GROUP_DELETE,
        PERMISSIONS.AMENITY_READ,
        PERMISSIONS.AMENITY_CREATE,
        PERMISSIONS.AMENITY_UPDATE,
        PERMISSIONS.AMENITY_DELETE,
        PERMISSIONS.ROOM_READ,
        PERMISSIONS.ROOM_UPDATE,
        PERMISSIONS.ROOM_TYPE_READ,
        PERMISSIONS.ROOM_TYPE_CREATE,
        PERMISSIONS.ROOM_TYPE_UPDATE,
        PERMISSIONS.ROOM_TYPE_DELETE,
        PERMISSIONS.INVENTORY_UPDATE,
        // Analytics integration configuration lives at the property level.
        PERMISSIONS.ANALYTICS_CONFIGURE,
    ],
    [MODULES.BOOKING]: [
        PERMISSIONS.RESERVATION_READ,
        PERMISSIONS.RESERVATION_WRITE,
        PERMISSIONS.RESERVATION_CANCEL,
        PERMISSIONS.GUEST_READ,
        PERMISSIONS.GUEST_WRITE,
        PERMISSIONS.AVAILABILITY_READ,
        PERMISSIONS.AVAILABILITY_UPDATE,
        PERMISSIONS.RESTRICTION_READ,
        PERMISSIONS.RESTRICTION_UPDATE,
        // Booking attempts are the abandoned-cart trail a reservation leaves
        // behind; they are read and followed up in the same workflow as the
        // reservation itself.
        PERMISSIONS.BOOKING_ATTEMPT_READ,
        PERMISSIONS.BOOKING_ATTEMPT_FOLLOWUP,
        PERMISSIONS.BOOKING_ATTEMPT_STATISTICS_READ,
    ],
    [MODULES.PRICING]: [
        PERMISSIONS.PRICING_READ,
        PERMISSIONS.PRICING_UPDATE,
        PERMISSIONS.CANCELLATION_POLICY_READ,
        PERMISSIONS.CANCELLATION_POLICY_CREATE,
        PERMISSIONS.CANCELLATION_POLICY_UPDATE,
        PERMISSIONS.CANCELLATION_POLICY_DELETE,
        PERMISSIONS.INCLUSION_READ,
        PERMISSIONS.INCLUSION_CREATE,
        PERMISSIONS.INCLUSION_UPDATE,
        PERMISSIONS.INCLUSION_DELETE,
        PERMISSIONS.TAX_READ,
        PERMISSIONS.TAX_CREATE,
        PERMISSIONS.TAX_UPDATE,
        PERMISSIONS.TAX_DELETE,
        PERMISSIONS.EXCHANGE_RATE_READ,
        PERMISSIONS.EXCHANGE_RATE_CREATE,
        PERMISSIONS.EXCHANGE_RATE_UPDATE,
        PERMISSIONS.EXCHANGE_RATE_DELETE,
    ],
    [MODULES.PROMOTIONS]: [
        PERMISSIONS.PROMOTION_READ,
        PERMISSIONS.PROMOTION_CREATE,
        PERMISSIONS.PROMOTION_UPDATE,
        PERMISSIONS.PROMOTION_DELETE,
    ],
    [MODULES.PAYMENTS]: [
        PERMISSIONS.PAYMENT_READ,
        PERMISSIONS.PAYMENT_WRITE,
        PERMISSIONS.PAYMENT_CAPTURE,
        PERMISSIONS.PAYMENT_REFUND,
        PERMISSIONS.PAYMENT_VOID,
        PERMISSIONS.PAYMENT_CONFIGURE,
        PERMISSIONS.PAYMENT_POLICY_READ,
        PERMISSIONS.PAYMENT_POLICY_CREATE,
        PERMISSIONS.PAYMENT_POLICY_UPDATE,
        PERMISSIONS.PAYMENT_POLICY_DELETE,
    ],
    [MODULES.RULES]: [
        PERMISSIONS.RULE_READ,
        PERMISSIONS.RULE_CREATE,
        PERMISSIONS.RULE_UPDATE,
        PERMISSIONS.RULE_DELETE,
    ],
    [MODULES.NOTIFICATIONS]: [
        PERMISSIONS.NOTIFICATION_READ,
        PERMISSIONS.NOTIFICATION_SEND,
        PERMISSIONS.NOTIFICATION_CONFIGURE,
    ],
    [MODULES.PMS]: [
        PERMISSIONS.PMS_READ,
        PERMISSIONS.PMS_CONFIGURE,
        PERMISSIONS.PMS_SYNC,
        PERMISSIONS.INTEGRATION_READ,
        PERMISSIONS.INTEGRATION_MANAGE,
        // Administering the chain/property/room-type/rate-plan mappings a PMS push
        // is resolved through. Same module: the mappings exist only because a PMS
        // does, and a tenant without one has nothing to map.
        PERMISSIONS.PMS_MAPPING_READ,
        PERMISSIONS.PMS_MAPPING_UPDATE,
    ],
};
//# sourceMappingURL=modules.js.map